import React, { useState, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  TooltipProvider,
} from "../../../ui/tooltip";

import { Skeleton } from "../../../ui/skeleton";

// Components
import InsightCard from "./InsightCard";
import Toolbar from "./Toolbar";
import EmptyState from "./EmptyState";
import UnifiedExtractorDialog from "../../../shared/AIExtractorDialog";
import { ExtractedItem, AIExtractedActionItem } from "../../../shared/AIExtractorDialog/types";
import InsightConnectionsPanel from "./InsightConnections";
import InsightStats from "./InsightStats";
import InsightDialog from "./InsightDialog";
import CategoryManagementDialog from "../actionItems/CategoryManagementDialog";
import AISynthesisPanel from "./AISynthesisPanel";
import InsightToActionDialog from "./InsightToActionDialog";
import InsightViewerDialog from "./InsightViewerDialog";

// Types
import {
  InsightViewType,
  InsightSortOption,
  InsightConnection,
  InsightStats as InsightStatsType,
} from "./types";

// API
import useRetryFetch from "../../../../hooks/useRetryFetch";
import {
  listTakeaways,
  deleteTakeaway,
  bulkAction,
  editTakeaway,
  createTakeaway,
  updateTakeawaySpaces,
  UserTakeaway,
} from "../../../../api/userTakeaways";
import { listCategories, createCategory, UserCategory } from "../../../../api/userCategories";
import { format, Locale } from "date-fns";
import { de, he } from "date-fns/locale";
import i18n from "i18next";

const dateFnsLocaleMap: Record<string, Locale> = { de, he };

interface InsightsProps {
  onCountChange?: (count: number) => void;
}

// Helper function to generate a varied category color
const generateCategoryColor = (): string => {
  const colors = [
    "#3B82F6", // blue
    "#8B5CF6", // violet
    "#EC4899", // pink
    "#10B981", // emerald
    "#F59E0B", // amber
    "#EF4444", // red
    "#06B6D4", // cyan
    "#6366F1", // indigo
    "#14B8A6", // teal
    "#F97316", // orange
    "#A855F7", // purple
    "#84CC16", // lime
  ];
  return colors[Math.floor(Math.random() * colors.length)];
};

const Insights: React.FC<InsightsProps> = ({ onCountChange }) => {
  const { t } = useTranslation();
  const retryFetch = useRetryFetch();
  const navigate = useNavigate();

  // Data state
  const [items, setItems] = useState<UserTakeaway[]>([]);
  const [categories, setCategories] = useState<UserCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [favorites, setFavorites] = useState<Set<string>>(new Set());

  // UI state
  const [viewType, setViewType] = useState<InsightViewType>("cards");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [sourceFilter, setSourceFilter] = useState<string>("all");
  const [sortOption, setSortOption] = useState<InsightSortOption>("newest");
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());

  // Dialog state
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [showItemDialog, setShowItemDialog] = useState(false);
  const itemDialogFromHub = useRef(false);
  const [showAIExtractor, setShowAIExtractor] = useState(false);
  const [showConnections, setShowConnections] = useState(false);
  const [showSynthesis, setShowSynthesis] = useState(false);
  const [showToAction, setShowToAction] = useState(false);
  const [editingItem, setEditingItem] = useState<UserTakeaway | undefined>(undefined);
  const [connectionsItem, setConnectionsItem] = useState<UserTakeaway | null>(null);
  const [connections, setConnections] = useState<InsightConnection[]>([]);
  const [actionInsight, setActionInsight] = useState<UserTakeaway | null>(null);

  // Viewer state for read-only insight viewing
  const [showViewer, setShowViewer] = useState(false);
  const [viewerInsights, setViewerInsights] = useState<UserTakeaway[]>([]);
  const [viewerThemeName, setViewerThemeName] = useState<string | undefined>(undefined);
  const [viewerInsight, setViewerInsight] = useState<UserTakeaway | null>(null);

  // Stats state (mock for now)
  const [stats, setStats] = useState<InsightStatsType>({
    total: 0,
    thisMonth: 0,
    lastMonth: 0,
    byCategory: [],
    bySource: [],
    byMonth: [],
    streak: { current: 0, longest: 0 },
  });

  // Load favorites from localStorage
  useEffect(() => {
    const savedFavorites = localStorage.getItem("insight_favorites");
    if (savedFavorites) {
      try {
        setFavorites(new Set(JSON.parse(savedFavorites)));
      } catch (e) {
        console.error("Failed to parse favorites:", e);
      }
    }
  }, []);

  // Persist favorites to localStorage
  useEffect(() => {
    localStorage.setItem("insight_favorites", JSON.stringify(Array.from(favorites)));
  }, [favorites]);

  useEffect(() => {
    fetchInsights();
    fetchCategories();
  }, []);

  // Listen for hub quick-action to auto-open create dialog
  useEffect(() => {
    const handler = () => {
      itemDialogFromHub.current = true;
      setEditingItem(undefined);
      setShowItemDialog(true);
    };
    window.addEventListener("workspace-open-add-insight", handler);
    return () => window.removeEventListener("workspace-open-add-insight", handler);
  }, []);

  useEffect(() => {
    fetchInsights();
  }, [statusFilter, categoryFilter, sourceFilter]);

  const fetchCategories = async () => {
    try {
      const response = await listCategories(retryFetch, "insight");
      setCategories(response.list);
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    }
  };

  const fetchInsights = async () => {
    setLoading(true);
    try {
      const filters: any = {
        type: "insight" as const,
      };

      if (statusFilter !== "all") {
        filters.status = statusFilter as any;
      }
      if (categoryFilter !== "all") {
        filters.category = categoryFilter;
      }
      if (sourceFilter !== "all") {
        filters.sourceType = sourceFilter as any;
      }
      if (searchQuery) {
        filters.search = searchQuery;
      }

      const response = await listTakeaways(retryFetch, filters);
      setItems(response.list);
      onCountChange?.(response.list.length);

      // Update stats
      updateStats(response.list);
    } catch (error) {
      console.error("Failed to fetch insights:", error);
      toast.error(t("failed_to_load_insights"));
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const updateStats = (insights: UserTakeaway[]) => {
    const now = new Date();
    const thisMonth = now.getMonth();
    const thisYear = now.getFullYear();
    const lastMonth = thisMonth === 0 ? 11 : thisMonth - 1;
    const lastMonthYear = thisMonth === 0 ? thisYear - 1 : thisYear;

    const thisMonthInsights = insights.filter((i) => {
      const d = new Date(i.createdOn);
      return d.getMonth() === thisMonth && d.getFullYear() === thisYear;
    });

    const lastMonthInsights = insights.filter((i) => {
      const d = new Date(i.createdOn);
      return d.getMonth() === lastMonth && d.getFullYear() === lastMonthYear;
    });

    // By category
    const categoryMap = new Map<string, { name: string; color: string; count: number }>();
    insights.forEach((i) => {
      if (i.category) {
        const existing = categoryMap.get(i.category._id);
        if (existing) {
          existing.count++;
        } else {
          categoryMap.set(i.category._id, {
            name: i.category.name,
            color: i.category.color,
            count: 1,
          });
        }
      }
    });

    // By source
    const sourceMap = new Map<string, number>();
    insights.forEach((i) => {
      const source = i.source.type;
      sourceMap.set(source, (sourceMap.get(source) || 0) + 1);
    });

    // By month (last 12 months)
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const byMonth: { month: string; count: number }[] = [];
    for (let i = 11; i >= 0; i--) {
      const d = new Date();
      d.setMonth(d.getMonth() - i);
      const month = d.getMonth();
      const year = d.getFullYear();
      const count = insights.filter((ins) => {
        const insDate = new Date(ins.createdOn);
        return insDate.getMonth() === month && insDate.getFullYear() === year;
      }).length;
      byMonth.push({ month: monthNames[month], count });
    }

    // Calculate streak (simplified)
    const sortedDates = Array.from(new Set(insights.map((i) => new Date(i.createdOn).toDateString())))
      .sort((a, b) => new Date(b).getTime() - new Date(a).getTime());

    let streak = 0;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (let i = 0; i < sortedDates.length; i++) {
      const checkDate = new Date(today);
      checkDate.setDate(checkDate.getDate() - i);
      if (sortedDates.includes(checkDate.toDateString())) {
        streak++;
      } else {
        break;
      }
    }

    setStats({
      total: insights.length,
      thisMonth: thisMonthInsights.length,
      lastMonth: lastMonthInsights.length,
      byCategory: Array.from(categoryMap.entries()).map(([id, data]) => ({
        categoryId: id,
        categoryName: data.name,
        categoryColor: data.color,
        count: data.count,
      })).sort((a, b) => b.count - a.count),
      bySource: Array.from(sourceMap.entries()).map(([source, count]) => ({
        source,
        count,
      })).sort((a, b) => b.count - a.count),
      byMonth,
      streak: {
        current: streak,
        longest: Math.max(streak, 0), // Would need to calculate from historical data
        lastInsightDate: sortedDates[0],
      },
    });
  };

  const deleteItem = async (itemId: string) => {
    try {
      const response = await deleteTakeaway(retryFetch, { id: itemId });

      if (response.status === "DELETED") {
        const newItems = items.filter((i) => i._id !== itemId);
        setItems(newItems);
        toast.success(t("insight_deleted"));
        onCountChange?.(newItems.length);
        updateStats(newItems);
      }
    } catch (error) {
      console.error("Failed to delete insight:", error);
      toast.error(t("failed_to_delete_insight"));
    }
  };

  const toggleSelectItem = (itemId: string) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
  };

  const toggleFavorite = (itemId: string) => {
    setFavorites((prev) => {
      const newFavorites = new Set(prev);
      if (newFavorites.has(itemId)) {
        newFavorites.delete(itemId);
      } else {
        newFavorites.add(itemId);
      }
      return newFavorites;
    });
  };

  const bulkArchive = async () => {
    try {
      const response = await bulkAction(retryFetch, {
        ids: Array.from(selectedItems),
        action: "archive",
      });

      if (response.status === "SUCCESS") {
        setItems(
          items.map((i) =>
            selectedItems.has(i._id) ? { ...i, status: "archived" as const } : i
          )
        );
        setSelectedItems(new Set());
        toast.success(t("items_archived"));
      }
    } catch (error) {
      console.error("Failed to archive items:", error);
      toast.error(t("failed_to_archive_items"));
    }
  };

  const bulkDelete = async () => {
    try {
      const response = await bulkAction(retryFetch, {
        ids: Array.from(selectedItems),
        action: "delete",
      });

      if (response.status === "SUCCESS") {
        const newItems = items.filter((i) => !selectedItems.has(i._id));
        setItems(newItems);
        setSelectedItems(new Set());
        toast.success(t("items_deleted"));
        onCountChange?.(newItems.length);
        updateStats(newItems);
      }
    } catch (error) {
      console.error("Failed to delete items:", error);
      toast.error(t("failed_to_delete_items"));
    }
  };

  const assignCategory = async (itemId: string, categoryId: string | null) => {
    try {
      const response = await editTakeaway(retryFetch, {
        id: itemId,
        category: categoryId,
      });

      if (response.status === "UPDATED") {
        setItems(
          items.map((i) =>
            i._id === itemId
              ? {
                  ...i,
                  category: categoryId
                    ? categories.find((c) => c._id === categoryId) || undefined
                    : undefined,
                }
              : i
          )
        );
        toast.success(t("category_assigned"));
      }
    } catch (error) {
      console.error("Failed to assign category:", error);
      toast.error(t("failed_to_assign_category"));
    }
  };

  const assignSpace = async (itemId: string, spaceIds: string[]) => {
    try {
      await updateTakeawaySpaces(retryFetch, itemId, spaceIds);
      setItems(
        items.map((i) =>
          i._id === itemId ? { ...i, spaceIds } : i
        )
      );
      toast.success(t("space_updated") || "Space assignment updated");
    } catch (error) {
      console.error("Failed to assign space:", error);
      toast.error(t("failed_to_assign_space") || "Failed to update space");
    }
  };

  const openEngagement = (item: UserTakeaway) => {
    if (item.source.fiProcess) {
      navigate(`/review?pid=${item.source.fiProcess}`);
    }
  };

  const handleAddItem = () => {
    setEditingItem(undefined);
    setShowItemDialog(true);
  };

  const handleEditItem = (item: UserTakeaway) => {
    setEditingItem(item);
    setShowItemDialog(true);
  };

  const handleSaveItem = async (data: { content: string; category?: string; spaceIds?: string[] }) => {
    try {
      if (editingItem) {
        const response = await editTakeaway(retryFetch, {
          id: editingItem._id,
          content: data.content,
          category: data.category || null,
          spaceIds: data.spaceIds,
        });

        if (response.status === "UPDATED") {
          setItems(
            items.map((i) =>
              i._id === editingItem._id
                ? {
                    ...i,
                    content: data.content,
                    category: data.category
                      ? categories.find((c) => c._id === data.category)
                      : undefined,
                    spaceIds: data.spaceIds,
                  }
                : i
            )
          );
          toast.success(t("insight_updated"));
        }
      } else {
        const response = await createTakeaway(retryFetch, {
          type: "insight",
          content: data.content,
          category: data.category || undefined,
          spaceIds: data.spaceIds,
          source: {
            type: "user_created",
          },
        });

        if (response.status === "CREATED" && response.takeaway) {
          const newItems = [response.takeaway, ...items];
          setItems(newItems);
          toast.success(t("insight_created"));
          onCountChange?.(newItems.length);
          updateStats(newItems);
          // Celebrate first insight
          window.dispatchEvent(
            new CustomEvent("clary:celebration", {
              detail: { key: "first_insight" },
            })
          );
        }
      }
      fetchInsights();
    } catch (error) {
      console.error("Failed to save insight:", error);
      toast.error(
        editingItem ? t("failed_to_update_insight") : t("failed_to_create_insight")
      );
      throw error;
    }
  };

  const handleAIExtractComplete = async (extracted: ExtractedItem[]) => {
    // Separate insights and action items
    const insights = extracted.filter((item) => item.type === "insight");
    const actionItems = extracted.filter((item) => item.type === "action");

    if (insights.length === 0 && actionItems.length === 0) {
      toast.info(t("no_items_selected") || "No items selected");
      return;
    }

    // Show loading toast
    const totalCount = insights.length + actionItems.length;
    const loadingToast = toast.loading(
      t("ai_extractor.creating_items", `Creating ${totalCount} items...`)
    );

    // Map suggested category names to IDs (or create new categories)
    const categoryNameToIdMap = new Map<string, string>();

    // Build map of existing category names to IDs
    for (const cat of categories) {
      categoryNameToIdMap.set(cat.name.toLowerCase(), cat._id);
    }

    // Create insights from AI extracted items
    let successCount = 0;
    let failCount = 0;

    for (const item of insights) {
      try {
        let categoryId: string | undefined = undefined;

        // If there's a suggested category name from AI, resolve it
        if (item.suggestedCategory) {
          // Check if suggestedCategory is already an ID (exists in categories array)
          const isAlreadyId = categories.some((cat) => cat._id === item.suggestedCategory);

          if (isAlreadyId) {
            categoryId = item.suggestedCategory;
          } else {
            // It's a name string from AI - try to find or create it
            const normalizedName = item.suggestedCategory.toLowerCase();

            if (categoryNameToIdMap.has(normalizedName)) {
              // Use existing category
              categoryId = categoryNameToIdMap.get(normalizedName);
            } else {
              // Create new category
              try {
                const newCatResponse = await createCategory(retryFetch, {
                  name: item.suggestedCategory,
                  type: "insight",
                  color: generateCategoryColor(),
                });
                if (newCatResponse.status === "CREATED" && newCatResponse._id) {
                  categoryId = newCatResponse._id;
                  categoryNameToIdMap.set(normalizedName, newCatResponse._id);
                  // Update local categories list
                  if (newCatResponse.category) {
                    setCategories((prev) => [...prev, newCatResponse.category!]);
                  }
                }
              } catch (error) {
                console.error("Failed to create category:", error);
                // Continue without category
              }
            }
          }
        }

        const response = await createTakeaway(retryFetch, {
          type: "insight",
          content: item.content,
          category: categoryId,
          source: {
            type: "user_created",
          },
        });
        if (response.status === "CREATED") {
          successCount++;
        } else {
          failCount++;
        }
      } catch (error) {
        console.error("Failed to create extracted insight:", error);
        failCount++;
      }
    }

    // Create action items
    let actionSuccessCount = 0;
    let actionFailCount = 0;

    for (const item of actionItems as AIExtractedActionItem[]) {
      try {
        let categoryId: string | undefined = undefined;

        // If there's a suggested category name from AI, resolve it
        if (item.suggestedCategory) {
          // Check if suggestedCategory is already an ID (exists in categories array)
          const isAlreadyId = categories.some((cat) => cat._id === item.suggestedCategory);

          if (isAlreadyId) {
            categoryId = item.suggestedCategory;
          } else {
            // It's a name string from AI - try to find or create it
            const normalizedName = item.suggestedCategory.toLowerCase();

            if (categoryNameToIdMap.has(normalizedName)) {
              // Use existing category
              categoryId = categoryNameToIdMap.get(normalizedName);
            } else {
              // Create new category
              try {
                const newCatResponse = await createCategory(retryFetch, {
                  name: item.suggestedCategory,
                  type: "action",
                  color: generateCategoryColor(),
                });
                if (newCatResponse.status === "CREATED" && newCatResponse._id) {
                  categoryId = newCatResponse._id;
                  categoryNameToIdMap.set(normalizedName, newCatResponse._id);
                  // Update local categories list
                  if (newCatResponse.category) {
                    setCategories((prev) => [...prev, newCatResponse.category!]);
                  }
                }
              } catch (error) {
                console.error("Failed to create category:", error);
                // Continue without category
              }
            }
          }
        }

        const response = await createTakeaway(retryFetch, {
          type: "action",
          content: item.content,
          category: categoryId,
          priority: item.suggestedPriority,
          dueDate: item.suggestedDueDate
            ? typeof item.suggestedDueDate === "number"
              ? item.suggestedDueDate
              : new Date(item.suggestedDueDate).getTime() || undefined
            : undefined,
          source: {
            type: "user_created",
          },
        });
        if (response.status === "CREATED") {
          actionSuccessCount++;
        } else {
          actionFailCount++;
        }
      } catch (error) {
        console.error("Failed to create extracted action:", error);
        actionFailCount++;
      }
    }

    // Dismiss loading toast
    toast.dismiss(loadingToast);

    // Show result
    const totalSuccess = successCount + actionSuccessCount;
    const totalFail = failCount + actionFailCount;

    if (totalSuccess > 0) {
      const messages: string[] = [];
      if (successCount > 0) {
        messages.push(`${successCount} ${t("insights", "insights")}`);
      }
      if (actionSuccessCount > 0) {
        messages.push(`${actionSuccessCount} ${t("action_items", "actions")}`);
      }
      toast.success(
        t("ai_extractor.items_created", `Created ${messages.join(" and ")}`)
      );
      // Refresh both insights and categories
      fetchInsights();
      fetchCategories();
    }

    if (totalFail > 0) {
      toast.error(
        t("ai_extractor.some_items_failed", `Failed to create ${totalFail} items`)
      );
    }
  };

  const handleViewConnections = (item: UserTakeaway) => {
    setConnectionsItem(item);
    // Mock connections - in production, call the API
    const mockConnections: InsightConnection[] = items
      .filter((i) => i._id !== item._id && i.category?._id === item.category?._id)
      .slice(0, 5)
      .map((i) => ({
        insightId: item._id,
        relatedInsightId: i._id,
        similarity: 0.6 + Math.random() * 0.35,
        connectionType: "category" as const,
        insight: i,
      }));
    setConnections(mockConnections);
    setShowConnections(true);
  };

  const handleConvertToAction = (item: UserTakeaway) => {
    setActionInsight(item);
    setShowToAction(true);
  };

  const handleViewThemeInsights = (themeName: string, insightIds: string[]) => {
    // Get full insight objects from IDs
    const themeInsights = items.filter((item) => insightIds.includes(item._id));
    if (themeInsights.length > 0) {
      setViewerThemeName(themeName);
      setViewerInsights(themeInsights);
      setViewerInsight(themeInsights[0]);
      setShowViewer(true);
    }
  };

  const handleCreateActions = async (
    actions: { content: string; priority?: "high" | "medium" | "low"; category?: string; dueDate?: number }[]
  ) => {
    let successCount = 0;
    for (const action of actions) {
      try {
        const response = await createTakeaway(retryFetch, {
          type: "action",
          content: action.content,
          priority: action.priority || "medium",
          category: action.category,
          dueDate: action.dueDate,
          source: {
            type: "user_created",
          },
        });
        if (response.status === "CREATED") {
          successCount++;
        }
      } catch (error) {
        console.error("Failed to create action:", error);
      }
    }

    if (successCount > 0) {
      toast.success(
        t("actions_created_count", { count: successCount }) ||
          `${successCount} action items created`
      );
    }
  };

  const handleClearFilters = () => {
    setStatusFilter("all");
    setCategoryFilter("all");
    setSourceFilter("all");
    setShowFavoritesOnly(false);
    setSearchQuery("");
  };

  // Filter and sort items
  const filteredItems = useMemo(() => {
    let result = [...items];

    // Apply filters
    if (statusFilter !== "all") {
      result = result.filter((i) => i.status === statusFilter);
    }
    if (categoryFilter !== "all") {
      result = result.filter((i) => i.category?._id === categoryFilter);
    }
    if (sourceFilter !== "all") {
      result = result.filter((i) => i.source.type === sourceFilter);
    }
    if (showFavoritesOnly) {
      result = result.filter((i) => favorites.has(i._id));
    }
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter((i) => i.content.toLowerCase().includes(query));
    }

    // Apply sorting
    switch (sortOption) {
      case "oldest":
        result.sort((a, b) => a.createdOn - b.createdOn);
        break;
      case "favorites_first":
        result.sort((a, b) => {
          const aFav = favorites.has(a._id) ? 1 : 0;
          const bFav = favorites.has(b._id) ? 1 : 0;
          if (bFav !== aFav) return bFav - aFav;
          return b.createdOn - a.createdOn;
        });
        break;
      case "newest":
      default:
        result.sort((a, b) => b.createdOn - a.createdOn);
        break;
    }

    return result;
  }, [items, statusFilter, categoryFilter, sourceFilter, showFavoritesOnly, searchQuery, sortOption, favorites]);

  const hasActiveFilters =
    statusFilter !== "all" ||
    categoryFilter !== "all" ||
    sourceFilter !== "all" ||
    showFavoritesOnly ||
    searchQuery !== "";

  return (
    <TooltipProvider delayDuration={100}>
      <div className="flex flex-col h-full space-y-4">
        {/* Toolbar */}
        <Toolbar
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          categoryFilter={categoryFilter}
          onCategoryFilterChange={setCategoryFilter}
          sourceFilter={sourceFilter}
          onSourceFilterChange={setSourceFilter}
          sortOption={sortOption}
          onSortChange={setSortOption}
          viewType={viewType}
          onViewTypeChange={setViewType}
          categories={categories}
          showFavoritesOnly={showFavoritesOnly}
          onShowFavoritesOnlyChange={setShowFavoritesOnly}
          onManageCategories={() => setShowCategoryDialog(true)}
          onAddInsight={handleAddItem}
          onAIExtract={() => setShowAIExtractor(true)}
          onAISynthesis={() => setShowSynthesis(true)}
          totalCount={filteredItems.length}
        />

        {/* Bulk Actions */}
        {selectedItems.size > 0 && (
          <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-100 rounded-lg">
            <span className="text-sm font-medium text-amber-900">
               {t("items_selected", {count: selectedItems.size})}
            </span>
            <div className="flex gap-2 ml-auto">
              <button
                onClick={bulkArchive}
                className="px-3 py-1.5 text-sm font-medium text-amber-700 hover:bg-amber-100 rounded-md transition-colors"
              >
                {t("archive")}
              </button>
              <button
                onClick={bulkDelete}
                className="px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 rounded-md transition-colors"
              >
                {t("delete")}
              </button>
              <button
                onClick={() => setSelectedItems(new Set())}
                className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
              >
                {t("cancel")}
              </button>
            </div>
          </div>
        )}

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto pb-4">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-1">
              {[1, 2, 3, 4, 5, 6].map(i => (
                <div key={i} className="p-4 border rounded-xl space-y-3">
                  <div className="flex items-center justify-between">
                    <Skeleton className="h-5 w-16 rounded-full" />
                    <Skeleton className="h-4 w-4 rounded" />
                  </div>
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                  <div className="flex items-center gap-2 pt-2">
                    <Skeleton className="h-3 w-20" />
                    <Skeleton className="h-3 w-16" />
                  </div>
                </div>
              ))}
            </div>
          ) : viewType === "stats" ? (
            <InsightStats
              stats={stats}
              onViewCategory={(catId) => {
                setCategoryFilter(catId);
                setViewType("cards");
              }}
              onViewSource={(source) => {
                setSourceFilter(source);
                setViewType("cards");
              }}
            />
          ) : filteredItems.length === 0 ? (
            <EmptyState
              hasFilters={hasActiveFilters}
              onAddInsight={handleAddItem}
              onAIExtract={() => setShowAIExtractor(true)}
              onRunEngagement={() => navigate("/portal")}
              onClearFilters={handleClearFilters}
            />
          ) : viewType === "timeline" ? (
            // Timeline view - grouped by month
            (() => {
              const dateLocale = dateFnsLocaleMap[i18n.language];
              const fmtOpts = dateLocale ? { locale: dateLocale } : undefined;
              const grouped = filteredItems.reduce<Record<string, UserTakeaway[]>>((acc, item) => {
                const key = format(new Date(item.createdOn), "MMMM yyyy", fmtOpts);
                if (!acc[key]) acc[key] = [];
                acc[key].push(item);
                return acc;
              }, {});

              return (
                <div className="space-y-8 max-w-3xl mx-auto">
                  {Object.entries(grouped).map(([month, monthItems]) => (
                    <div key={month}>
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-3 h-3 rounded-full bg-amber-400 border-2 border-amber-200 flex-shrink-0" />
                        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">{month}</h3>
                        <span className="text-xs text-muted-foreground">({monthItems.length})</span>
                        <div className="flex-1 h-px bg-gray-200" />
                      </div>
                      <div className="relative border-s-2 border-gray-200 ms-1.5 ps-6 space-y-4">
                        {monthItems.map((item) => (
                          <div key={item._id} className="relative">
                            <div className="absolute -start-[31px] top-3 w-2.5 h-2.5 rounded-full bg-white border-2 border-amber-400" />
                            <div className="text-xs text-muted-foreground mb-1">
                              {format(new Date(item.createdOn), "MMM d, yyyy", fmtOpts)}
                            </div>
                            <InsightCard
                              insight={item}
                              categories={categories}
                              isSelected={selectedItems.has(item._id)}
                              isFavorite={favorites.has(item._id)}
                              connectionCount={
                                items.filter(
                                  (i) =>
                                    i._id !== item._id &&
                                    i.category?._id === item.category?._id &&
                                    item.category
                                ).length
                              }
                              onSelect={toggleSelectItem}
                              onToggleFavorite={toggleFavorite}
                              onEdit={handleEditItem}
                              onDelete={deleteItem}
                              onAssignCategory={assignCategory}
                              onAssignSpace={assignSpace}
                              onViewEngagement={openEngagement}
                              onViewConnections={handleViewConnections}
                              onConvertToAction={handleConvertToAction}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {filteredItems.map((item) => (
                <InsightCard
                  key={item._id}
                  insight={item}
                  categories={categories}
                  isSelected={selectedItems.has(item._id)}
                  isFavorite={favorites.has(item._id)}
                  connectionCount={
                    items.filter(
                      (i) =>
                        i._id !== item._id &&
                        i.category?._id === item.category?._id &&
                        item.category
                    ).length
                  }
                  onSelect={toggleSelectItem}
                  onToggleFavorite={toggleFavorite}
                  onEdit={handleEditItem}
                  onDelete={deleteItem}
                  onAssignCategory={assignCategory}
                  onAssignSpace={assignSpace}
                  onViewEngagement={openEngagement}
                  onViewConnections={handleViewConnections}
                  onConvertToAction={handleConvertToAction}
                />
              ))}
            </div>
          )}
        </div>

        {/* Dialogs */}
        <CategoryManagementDialog
          open={showCategoryDialog}
          onOpenChange={setShowCategoryDialog}
          type="insight"
        />

        <InsightDialog
          open={showItemDialog}
          onOpenChange={(open) => {
            setShowItemDialog(open);
            if (!open && itemDialogFromHub.current) {
              itemDialogFromHub.current = false;
              window.dispatchEvent(new Event("workspace-return-to-hub"));
            }
          }}
          onSave={handleSaveItem}
          categories={categories}
          item={editingItem}
          onCategoryCreated={(newCat) => {
            setCategories((prev) => [...prev, newCat]);
          }}
        />

        <UnifiedExtractorDialog
          open={showAIExtractor}
          onOpenChange={setShowAIExtractor}
          onExtractComplete={handleAIExtractComplete}
          categories={categories}
          mode="both"
        />

        <InsightConnectionsPanel
          open={showConnections}
          onOpenChange={setShowConnections}
          insight={connectionsItem}
          connections={connections}
          onViewInsight={(id) => {
            const insight = items.find((i) => i._id === id);
            if (insight) {
              setConnectionsItem(insight);
              handleViewConnections(insight);
            }
          }}
        />

        <AISynthesisPanel
          open={showSynthesis}
          onOpenChange={setShowSynthesis}
          insights={items}
          onViewThemeInsights={handleViewThemeInsights}
          onViewCategory={(categoryId) => {
            setShowSynthesis(false);
            setCategoryFilter(categoryId);
          }}
        />

        <InsightToActionDialog
          open={showToAction}
          onOpenChange={setShowToAction}
          insight={actionInsight}
          categories={categories}
          onCreateActions={handleCreateActions}
        />

        <InsightViewerDialog
          open={showViewer}
          onOpenChange={setShowViewer}
          insight={viewerInsight}
          insights={viewerInsights}
          themeName={viewerThemeName}
          onEdit={(insight) => {
            setShowViewer(false);
            handleEditItem(insight);
          }}
          onConvertToAction={(insight) => {
            setShowViewer(false);
            handleConvertToAction(insight);
          }}
          onViewEngagement={(engagementId) => {
            navigate(`/review?pid=${engagementId}`);
          }}
          onToggleFavorite={toggleFavorite}
          isFavorite={viewerInsight ? favorites.has(viewerInsight._id) : false}
          onNavigate={(insight) => setViewerInsight(insight)}
        />
      </div>
    </TooltipProvider>
  );
};

export default Insights;
